import React, { useMemo, useState } from "react";
import {
  EuiBreadcrumb,
  EuiPage,
  EuiPageBody,
  EuiPageContent,
  EuiPageContentBody,
  EuiPageHeader,
  EuiSpacer,
  EuiTabbedContent,
  EuiTabbedContentTab,
} from "@elastic/eui";
import { PageErrorBoundary } from "src/pages/status";
import { Header, SideNav } from "../";
import { useAnketaI18n } from "src/internationalization";
import { AdminPermissionsEditor } from "./permissions";
import { AdminUsersRoles } from "./users";
import { AdminExternalAccess } from "./external";
import { AdminSyslogDestinations } from "./syslog";
import { AdminAiAlertDestinations } from "./ai-alerts";
import { AdminSettings } from "./settings";
import { AdminSimarglCredential } from "./simargl";
import { AdminMailTemplates } from "./mail";

/**
 * Superuser-only Admin area. Mirrors the OpsanioHomePage layout (EuiPage +
 * SideNav + Header) and hosts two tabs: the ABAC permission matrix editor and the
 * user=>role assignment screen. Gated in navigation/routes by `user.isSudo`; the
 * backend re-enforces superuser status on every /api/v2/admin request.
 */
export const AdminHomePage = ({
  breadcrumbs,
}: {
  breadcrumbs: EuiBreadcrumb[];
}): JSX.Element => {
  const { translate } = useAnketaI18n();

  const initialNavIsOpen = useMemo(() => {
    const stored = localStorage.getItem("anketa.nav.side.isOpen");
    return stored ? stored === "true" : false;
  }, []);

  const [navIsOpen, setNavIsOpen] = useState<boolean>(initialNavIsOpen);

  const tabs: EuiTabbedContentTab[] = [
    {
      id: "permissions",
      name: translate("anketa.admin.tab.permissions", {
        defaultMessage: "Permissions",
      }),
      content: (
        <>
          <EuiSpacer size="m" />
          <AdminPermissionsEditor />
        </>
      ),
    },
    {
      id: "users",
      name: translate("anketa.admin.tab.users", {
        defaultMessage: "Users & roles",
      }),
      content: (
        <>
          <EuiSpacer size="m" />
          <AdminUsersRoles />
        </>
      ),
    },
    {
      id: "external",
      name: translate("anketa.admin.tab.external", {
        defaultMessage: "External access",
      }),
      content: (
        <>
          <EuiSpacer size="m" />
          <AdminExternalAccess />
        </>
      ),
    },
    {
      id: "syslog",
      name: "Syslog / Wazuh",
      content: (
        <>
          <EuiSpacer size="m" />
          <AdminSyslogDestinations />
        </>
      ),
    },
    {
      id: "settings",
      name: translate("anketa.admin.tab.settings", {
        defaultMessage: "Settings",
      }),
      content: (
        <>
          <EuiSpacer size="m" />
          <AdminSettings />
        </>
      ),
    },
    {
      id: "hardware",
      name: translate("anketa.admin.tab.hardware", {
        defaultMessage: "Hardware (NetBox)",
      }),
      content: (
        <>
          <EuiSpacer size="m" />
          {/*
            Which NetBox device type each kit line means. The hardware request
            mail orders by the part number, model name and description of these
            types, so replacing a server generation is a field edit here rather
            than a release.
          */}
          <AdminSettings group="hardware" />
        </>
      ),
    },
    {
      id: "dokumente",
      name: translate("anketa.admin.tab.dokumente", {
        defaultMessage: "Dokumente",
      }),
      content: (
        <>
          <EuiSpacer size="m" />
          {/*
            The contractual papers the generated appliance documentation refers
            to instead of restating. One set per installation - they vary per
            contract, not per questionnaire, which is why they are settings and
            not questionnaire fields.
          */}
          <AdminSettings group="dokumente" />
        </>
      ),
    },
    {
      id: "aiAlerts",
      name: translate("anketa.admin.tab.aiAlerts", {
        defaultMessage: "AI cost alerts",
      }),
      content: (
        <>
          <EuiSpacer size="m" />
          <AdminAiAlertDestinations />
        </>
      ),
    },
    {
      id: "mail",
      name: translate("anketa.admin.tab.mail", {
        defaultMessage: "E-Mail",
      }),
      content: (
        <>
          <EuiSpacer size="m" />
          <AdminMailTemplates />
        </>
      ),
    },
    {
      id: "simargl",
      name: translate("anketa.admin.tab.simargl", {
        defaultMessage: "Simargl S3",
      }),
      content: (
        <>
          <EuiSpacer size="m" />
          <AdminSimarglCredential />
        </>
      ),
    },
  ];

  return (
    <EuiPage>
      {navIsOpen && <SideNav />}
      <EuiPageBody>
        <Header
          breadcrumbs={breadcrumbs}
          navIsOpen={navIsOpen}
          setNavIsOpen={setNavIsOpen}
          updateCatalog={() => undefined}
        />
        <EuiPageContent>
          <EuiPageHeader
            pageTitle={translate("anketa.admin.page.title", {
              defaultMessage: "Administration",
            })}
          />
          <EuiPageContentBody>
            <PageErrorBoundary>
              <EuiTabbedContent tabs={tabs} initialSelectedTab={tabs[0]} />
            </PageErrorBoundary>
          </EuiPageContentBody>
        </EuiPageContent>
      </EuiPageBody>
    </EuiPage>
  );
};
